
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  description: 'Learn how to use Bank Transfers webhooks to receive micro-deposit status updates'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Micro-deposit events' subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`If you are using the optional `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/same-day/"
      }}>{`Same-Day Micro-deposits`}</a>{` verification method for Auth and want to receive updates on the state of your micro-deposit transfers, you can enable webhooks for Bank Transfer events that notify you of transfer status updates for Plaid-initiated transfers within the ACH network. `}</p>
    <h4 {...{
      "id": "bank-transfers-webhooks"
    }}>{`Bank Transfers webhooks`}</h4>
    <p>{`To enable Bank Transfers webhooks, add your endpoint on the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/webhooks"
      }}>{`account webhooks`}</a>{` page of the dashboard. You will need to have received production approval for Auth before being able to add an endpoint.  `}</p>
    <Callout mdxType="Callout">
      <p>{`Bank Transfers webhooks are not part of Plaid's `}<a parentName="p" {...{
          "href": "/docs/transfer"
        }}>{`Transfer`}</a>{` product. All Auth customers have access to Bank Transfer webhooks; it is not required to sign up for Plaid Transfer to use these webhooks.`}</p>
    </Callout>
    <p>{`To confirm that your endpoint has been correctly configured, you can trigger a test webhook via `}<inlineCode parentName="p">{`/sandbox/bank_transfer/fire_webhook`}</inlineCode>{`. You should receive the payload body specified below.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Bank Transfers webhook body",
        "#Bank": true,
        "Transfers": true,
        "webhook": true,
        "body": true
      }}>{`{
  "webhook_type": "BANK_TRANSFERS",
  "webhook_code": "BANK_TRANSFERS_EVENTS_UPDATE"
}
`}</code></pre>
    <p>{`Once you have enabled Bank Transfers webhooks, the `}<inlineCode parentName="p">{`/bank_transfer/event/sync`}</inlineCode>{` endpoint can be called to discover new ACH events. To know when you should call this endpoint, listen for the `}<a parentName="p" {...{
        "href": "/docs/api/products/auth/#bank_transfers_events_update"
      }}><inlineCode parentName="a">{`BANK_TRANSFERS_EVENTS_UPDATE`}</inlineCode></a>{` webhook.
You will receive a Bank Transfers webhook any time you have posted or returned ACH micro-deposit events available. You can also search or filter micro-deposit events using the `}<inlineCode parentName="p">{`/bank_transfer/event/list`}</inlineCode>{` endpoint.`}</p>
    <p>{`Bank Transfers webhooks and the Bank Transfers endpoint will reflect any ACH micro-deposit sent by Plaid, including both Same-Day Micro-deposits and Automated Micro-deposits, if enabled. (They will `}<em parentName="p">{`not`}</em>{` reflect Instant Micro-deposits, which are sent via the RTP / FedNow rails rather than via ACH and are delivered and verified instantly within a single Link session.) Bank Transfers webhooks and endpoints will only reflect data about ACH events initiated through Plaid. They do not reflect other ACH activity on a linked account. `}</p>
    <h4 {...{
      "id": "event-types"
    }}>{`Event types`}</h4>
    <p>{`Once your user successfully completes the micro-deposit Link flow, you will receive an `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` in the success callback.
Bank Transfers events also contain an `}<inlineCode parentName="p">{`account_id`}</inlineCode>{`, which you should use to connect events to the corresponding user.`}</p>
    <Callout mdxType="Callout">
      <p>{`If an end user links the same account via Same-Day Micro-deposits again while an earlier micro-deposit to that account is still pending, Plaid reuses the earlier micro-deposit instead of sending a new one. Events for the reused micro-deposit will contain only the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{` from the original Item. To associate these events with the newer Item, match on the account and routing number rather than the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{`.`}</p>
    </Callout>
    <h5 {...{
      "id": "pending"
    }}>{`Pending`}</h5>
    <p>{`A pending event type means that we have a record of the micro-deposit in our systems, but it has not yet been sent.
You may assume that micro-deposits are in a pending state once you `}<a parentName="p" {...{
        "href": "/docs/api/items/#itempublic_tokenexchange"
      }}>{`exchange`}</a>{` the corresponding Item's public token for an access token.
Note that Plaid does not send webhooks for new pending events, but you will still see pending events in event sync responses.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Example pending event",
        "#Example": true,
        "pending": true,
        "event": true
      }}>{`{
  "account_id": "3MqrrrP5pWUGgmnvaQlPu19R6DvwPRHwNbLr9",
  "bank_transfer_amount": "0.01",
  "bank_transfer_id": "5645fe0e-bd5e-d8da-828b-e2c7540c69d8",
  "bank_transfer_iso_currency_code": "USD",
  "bank_transfer_type": "credit",
  "direction": "outbound",
  "event_id": 5,
  "event_type": "pending",
  "failure_reason": null,
  "origination_account_id": null,
  "receiver_details": null,
  "timestamp": "2021-03-22T18:52:02Z"
}
`}</code></pre>
    <h6 {...{
      "id": "recommended-action"
    }}>{`Recommended action`}</h6>
    <p>{`No action needed`}</p>
    <h5 {...{
      "id": "posted"
    }}>{`Posted`}</h5>
    <p>{`For successful micro-deposit transfers, `}<inlineCode parentName="p">{`posted`}</inlineCode>{` events are the terminal event type, and no more events will be issued.
Note that the end user may not receive the micro-deposit until several banking hours after the `}<inlineCode parentName="p">{`posted`}</inlineCode>{` event.
In addition, a `}<inlineCode parentName="p">{`posted`}</inlineCode>{` event does not guarantee that the micro-deposit was successful; if the micro-deposit fails, a `}<inlineCode parentName="p">{`reversed`}</inlineCode>{` event will eventually occur some time after the `}<inlineCode parentName="p">{`posted`}</inlineCode>{` event.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Example posted event",
        "#Example": true,
        "posted": true,
        "event": true
      }}>{`{
  "account_id": "3MqrrrP5pWUGgmnvaQlPu19R6DvwPRHwNbLr9",
  "bank_transfer_amount": "0.01",
  "bank_transfer_id": "5645fe0e-bd5e-d8da-828b-e2c7540c69d8",
  "bank_transfer_iso_currency_code": "USD",
  "bank_transfer_type": "credit",
  "direction": "outbound",
  "event_id": 6,
  "event_type": "posted",
  "failure_reason": null,
  "origination_account_id": null,
  "receiver_details": null,
  "timestamp": "2021-03-22T18:52:02Z"
}
`}</code></pre>
    <h6 {...{
      "id": "recommended-action-1"
    }}>{`Recommended action`}</h6>
    <p>{`If the micro-deposit succeeds, all posted transfers will land in the end user's account by 8:30 AM ET on the following banking day.`}</p>
    <p>{`After the micro-deposit settlement time, Plaid recommends sending the end user an alert (through SMS, email, or push notification) to verify the micro-deposit amounts. `}</p>
    <p>{`If you have enabled both Same Day and Automated micro-deposits, then before notifying the user, you should first confirm that the event corresponds to a Same Day micro-deposit by checking that the verification status is `}<inlineCode parentName="p">{`pending_manual_verification`}</inlineCode>{` (and not `}<inlineCode parentName="p">{`pending_automatic_verification`}</inlineCode>{`, which would correspond to an Automated Micro-deposit). Because Plaid handles Automated Micro-deposits without user interaction, it is not necessary to prompt the user during the Automatic Micro-deposit flow.`}</p>
    <p>{`If you have not already stored the verification status, you can obtain it by calling `}<inlineCode parentName="p">{`/accounts/get`}</inlineCode>{`. `}</p>
    <h5 {...{
      "id": "reversed"
    }}>{`Reversed`}</h5>
    <p>{`A `}<inlineCode parentName="p">{`reversed`}</inlineCode>{` event indicates that a micro-deposit attempt has failed.
Reversed events will contain an `}<a parentName="p" {...{
        "href": "/docs/errors/transfer/#ach-return-codes"
      }}>{`ACH return code`}</a>{` that indicates why the micro-deposit failed.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#Example reversed event",
        "#Example": true,
        "reversed": true,
        "event": true
      }}>{`{
  "account_id": "bV8WNn73rLI5Ln1MmdErsDn9jv7w37uGMaQvP",
  "bank_transfer_amount": "0.01",
  "bank_transfer_id": "826712b2-c707-cf98-5ba9-13bd3cc2b2f0",
  "bank_transfer_iso_currency_code": "USD",
  "bank_transfer_type": "credit",
  "direction": "outbound",
  "event_id": 5,
  "event_type": "reversed",
  "failure_reason": {
    "ach_return_code": "R03",
    "description": "No account or unable to locate account"
  },
  "origination_account_id": null,
  "receiver_details": null,
  "timestamp": "2021-03-25T21:35:47Z"
}
`}</code></pre>
    <h6 {...{
      "id": "recommended-action-2"
    }}>{`Recommended action`}</h6>
    <p>{`Contact the user with a notification that authentication has failed. Once they return to your application, restart the Link flow to begin another authentication attempt.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "bank-transfers-webhooks",
        level: 4,
        title: "Bank Transfers webhooks",
      },
{
        id: "event-types",
        level: 4,
        title: "Event types",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
